import Layout from "../../components/Layout";
import PageHeader from '../PageHeader/PageHeader';
import { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate, Link } from 'react-router-dom';
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { classes } from "../../util/style-utils";
import '../../styles/Sweeps.css';
import { ClientConfigurationContext } from "../../context/ClientConfigurationContextProvider";
import { getAgentProfile } from '../../api/agent-profile';
import { UserContext } from '../../context/UserContextProvider';
import { generateSweepDocument } from "../../api/docusign";
import { updateSweepDetails, getAgencyProfile } from "../../api/agency-profile";
import { logDetails } from "../../api/logger-configuration";
import Loader, { Types } from "../gdk/Loader";
import EmailManagement from "./EmailManagement";
import { EMAIL_WITH_TLD_REGEX } from "../../constants/ValidationConstants";

const validationSchema = yup.object({
    emailAddress: yup.string()
        .nullable()
        .email("Please enter a valid email address.")
        .matches(EMAIL_WITH_TLD_REGEX, "Please enter a valid email address.")
        .required('Email address is required.'),
})

const EmailSettings = () => {
    const {
        register,
        formState: { errors },
        trigger,
        setValue,
        clearErrors
    } = useForm({
        resolver: yupResolver(validationSchema)
    });
    const user = useContext(UserContext);
    const clientConfiguration = useContext(ClientConfigurationContext);
    const location = useLocation();
    const [agencyProfile, setAgencyProfile] = useState();
    const [agentProfile, setAgentProfile] = useState();
    const [branchProfile, setBranchProfile] = useState();
    const [isCheckedConsent, setIsCheckedConsent] = useState(false);
    const [agencyPrincipalEmail, setAgencyPrincipalEmail] = useState("");
    const [isBranch, setIsBranch] = useState(false);
    const [isDocusignCompleted, setIsDocusignCompleted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageReload, setPageReload] = useState(false);
    const navigate = useNavigate();
    const [isDocusignIncomplete, setIsDocusignIncomplete] = useState(false);

    const handleCheckboxChange = () => {
        setIsCheckedConsent(!isCheckedConsent);
    }

    const handleEmailChange = (e) => {
        setAgencyPrincipalEmail(e.target.value);
        setValue('emailAddress', e.target.value);

        // Clear any existing validation errors when user starts typing
        if (errors.emailAddress) {
            clearErrors('emailAddress');
        }
    }

    const handleDocusign = async (e) => {
        e.preventDefault();
        // Set the current email value in the form and Trigger validation
        setValue('emailAddress', agencyPrincipalEmail || '');
        const isValid = await trigger('emailAddress');

        if (!isValid) {
            // Validation failed, errors will be displayed automatically
            return;
        }

        var agentName = agentProfile.firstName + " " + agentProfile.lastName;
        let postDocusignUrl = "";
        if (isBranch) {
            postDocusignUrl = `isBranchDocusign=true&branchAgencyId=${branchProfile.agencyId}&sweepEmail=${agencyPrincipalEmail}`;
        }
        else {
            postDocusignUrl = `isSweepDocusign=true&sweepEmail=${agencyPrincipalEmail}`;
        }

        try {
            let address = await getSweepDocumentAddress(agencyProfile, branchProfile, isBranch);

            var generateSweepDocumentRequest = {
                "agentName": agentName,
                "printedName": agentName,
                "title": agentProfile.jobProfile ?? "Principal",
                "firstName": agentProfile.firstName,
                "lastName": agentProfile.lastName,
                "email": agencyPrincipalEmail,
                "city": address.city,
                "state": address.state,
                "zip": address.zipCode,
                "postDocusignUrl": postDocusignUrl
            };
            const success = (res) => {
                window.location.href = res.docServiceUrl;
            }

            const failure = () => {
                setLoading(false);
            }

            setLoading(true);
            generateSweepDocument(generateSweepDocumentRequest, success, failure);
        } catch (error) {
            console.error('Error generating sweep document:', error);
            setLoading(false);
        }
    }

    const getSweepDocumentAddress = async (agencyProfile, branchProfile, isBranch) => {
        // Helper function to check if address has valid (non-null) values
        const isValidAddress = (address) =>
            address?.city?.trim() &&
            address?.state?.trim() &&
            address?.zipCode?.trim();

        // Define address priority order based on branch status
        const addressCandidates = isBranch
            ? [
                branchProfile?.businessAddress,
                agencyProfile?.businessAddress,
                agentProfile?.addresses?.businessAddress
            ]
            : [
                agencyProfile?.businessAddress,
                agentProfile?.addresses?.businessAddress
            ];

        // Find first valid address
        const selectedAddress = addressCandidates.find(isValidAddress);

        if (!selectedAddress) {
            throw new Error('No valid address found for document generation');
        }

        return {
            city: selectedAddress.city,
            state: selectedAddress.state,
            zipCode: selectedAddress.zipCode
        };
    }

    const handleNextClick = () => {
        if (isBranch) {
            navigate("/sweep-account-settings", { state: { agencyProfile: agencyProfile, branchProfile: branchProfile, isBranch: isBranch } });
        }
        else {
            navigate("/sweep-account-settings", { state: { agencyProfile: agencyProfile } });
        }
    }


    // Render the appropriate email component based on DocuSign completion status
    const renderEmailComponent = () => {
        if (isDocusignCompleted) {
            if (isBranch) {
                return (
                    <EmailManagement
                        agencyProfile={branchProfile}
                        setAgencyProfile={setBranchProfile}
                        isSweepsOnboarding={true}
                    />
                );
            } else {
                return (
                    <EmailManagement
                        agencyProfile={agencyProfile}
                        setAgencyProfile={setAgencyProfile}
                        isSweepsOnboarding={true}
                    />
                );
            }
        } else {
            return (
                <div className="flexible-container sweeps-email-settings">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className={classes("form-field", (errors.emailAddress) && "form-field--error")}>
                                <label htmlFor="email" className="text">Email Address</label>
                                <input
                                    id="email"
                                    type="text"
                                    className="email-input"
                                    {...register("emailAddress")}
                                    onChange={(e) => handleEmailChange(e)}
                                />
                                {errors.emailAddress &&
                                    <span className="form-message">{errors.emailAddress.message}</span>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    };

    useEffect(() => {
        const disclaimer = document.querySelector("gds-disclaimer");

        if (disclaimer) {
            // Access the Shadow DOM
            const shadowRoot = disclaimer.shadowRoot;

            if (shadowRoot) {
                // Target the content container inside the Shadow DOM
                const contentContainer = shadowRoot.querySelector(".content-container");

                if (contentContainer) {
                    // Remove scrolling and adjust size based on content
                    contentContainer.style.overflow = "visible";
                    contentContainer.style.maxHeight = "none";
                    contentContainer.style.height = "auto";
                }
            }
        }
    }, []);

    // Helper function to update sweep details for both agency and branch profiles
    const updateProfileSweepDetails = (profile, profileType, email) => {
        const updatedProfile = {
            ...profile,
            ivans: profile.ivans ?? {},
            sweepDetails: {
                ...profile.sweepDetails,
                SweepsEmail: email,
            }
        };

        const success = () => {
            console.log("Profile Type: ", profileType);
            logDetails(`Successfully updated sweep email for ${profileType} after Docusign: ${updatedProfile.agencyId}`);
            if (profileType === "Branch") {
                setBranchProfile(updatedProfile);
            } else {
                setAgencyProfile(updatedProfile);
            }
            setPageReload(false);
            setIsDocusignCompleted(true);
        };

        const failure = () => {
            logDetails(`Failed to update sweep email for ${profileType} after Docusign: ${updatedProfile.agencyId}`);
            setPageReload(false);
        };

        updateSweepDetails(updatedProfile, success, failure);
    };



    // Helper function to load agency profile
    const loadAgencyProfile = (agencyId) => {
        return new Promise((resolve) => {
            getAgencyProfile(agencyId, (agencyProfile) => {
                setAgencyProfile(agencyProfile);
                setAgencyPrincipalEmail(agencyProfile.principal.email);

                if (agencyProfile.sweepDetails?.SweepsEmail) {
                    setIsDocusignCompleted(true);
                    setAgencyPrincipalEmail(agencyProfile.sweepDetails.SweepsEmail);
                }

                resolve(agencyProfile);
            });
        });
    };

    // Helper function to load branch profile
    const loadBranchProfile = (agencyId) => {
        return new Promise((resolve) => {
            getAgencyProfile(agencyId, (branchResponse) => {
                setBranchProfile(branchResponse);
                setAgencyPrincipalEmail(branchResponse.branchContact.email);

                if (branchResponse.sweepDetails?.SweepsEmail) {
                    setIsDocusignCompleted(true);
                    setAgencyPrincipalEmail(branchResponse.sweepDetails.SweepsEmail);
                } else {
                    setIsDocusignCompleted(false);
                }

                resolve(branchResponse);
            });
        });
    };

    useEffect(() => {
        setPageReload(true);
        setIsBranch(location.state?.isBranch);

        const initializeProfiles = async () => {
            try {
                // Load agent profile
                getAgentProfile(user?.agentId, (agentProfile) => {
                    setAgentProfile(agentProfile);
                });

                // Load agency profile if provided
                if (location.state?.agencyProfile) {
                    await loadAgencyProfile(location.state.agencyProfile.agencyId);
                }

                // Load branch profile if provided
                if (location.state?.isBranch && location.state?.branchProfile) {
                    await loadBranchProfile(location.state.branchProfile.agencyId);
                }

                // Check for incomplete docusign
                if (location.state?.redirectFromDocusign && !location.state?.isDocusignCompleted) {
                    setIsDocusignIncomplete(true);
                }

            } finally {
                setPageReload(false);
            }
        };

        initializeProfiles();
    }, []);

    // Helper function to check if docusign update conditions are met
    const shouldUpdateDocusignProfile = () => {
        return location.state?.redirectFromDocusign &&
            location.state?.isDocusignCompleted &&
            location.state?.sweepEmail &&
            !isDocusignCompleted &&
            !pageReload &&
            (agencyProfile || branchProfile);
    };

    // Helper function to handle docusign profile update
    const handleDocusignProfileUpdate = () => {
        setPageReload(true);
        setAgencyPrincipalEmail(location.state.sweepEmail);

        if (location.state?.isBranch && branchProfile) {
            updateProfileSweepDetails(branchProfile, "Branch", location.state.sweepEmail);
        } else if (agencyProfile) {
            updateProfileSweepDetails(agencyProfile, "Agency", location.state.sweepEmail);
        } else {
            setPageReload(false);
        }
    };

    useEffect(() => {
        if (shouldUpdateDocusignProfile()) {
            handleDocusignProfileUpdate();
        }
    }, [location.state?.sweepEmail, location.state?.redirectFromDocusign, location.state?.isDocusignCompleted, isDocusignCompleted, pageReload, agencyProfile, branchProfile]);

    // Sync React Hook Form with agencyPrincipalEmail state
    useEffect(() => {
        if (agencyPrincipalEmail) {
            setValue('emailAddress', agencyPrincipalEmail);
        }
    }, [agencyPrincipalEmail, setValue]);

    if (loading || pageReload) {
        const message = pageReload
            ? "Thank you for your patience."
            : <>We are processing your form.<br />Thank you for your patience.</>;

        return <Loader type={Types.Section}>{message}</Loader>
    }

    return (
        <Layout>
            <PageHeader
                header="Sweep Account Management"
                showBottomBorder={false}
                children={
                    <p>
                        Review and edit information related to your GEICO agency profile and sweep accounts. Some changes can only be made by an administrator or with the National Producer Database.
                    </p>
                }
            />
            <div className="container margin-top sweeps">
                <gds-progress-bar-group>
                    <gds-progress-bar value="50">Email Settings</gds-progress-bar>
                    <gds-progress-bar value="0">Sweep Account</gds-progress-bar>
                </gds-progress-bar-group>
                <div className="flexible-container sweeps-email-settings">
                    <div className="row">
                        <div className="col-sm-12">
                            <h3>Email Information and Disclosure</h3>
                        </div>
                    </div>
                    <span className="descriptive-copy">Enter the email adress you'd like to use to receive notifications about your sweep account.</span>
                    <br />
                    <br />
                    {renderEmailComponent()}
                    {clientConfiguration && clientConfiguration.enableSweepDocuSign && !isDocusignCompleted && !isDocusignIncomplete && (
                        <div>
                            <div className="row">
                                <div className="alert">
                                    <div className="alert--low-importance">
                                        <span className="icon-info"></span>
                                        <ul className="alert-list">
                                            <li className="alert-content">
                                                <div className="h4">Signature required</div>
                                                <p>To Continue, please sign the GEICO Producer Agreement and Sweep Acount Authorization Form. Select the link below to provide your signature via Docusign.</p>
                                                <a onClick={(e) => handleDocusign(e)}>Sign Authorization Form</a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {clientConfiguration && clientConfiguration.enableSweepDocuSign && isDocusignCompleted && (
                        <div>
                            <div className="row">
                                <div className="alert">
                                    <div className="alert--low-importance">
                                        <span className="icon-info"></span>
                                        <ul className="alert-list">
                                            <li className="alert-content">
                                                <div class="h4">You completed your docusign form!</div>
                                                <p>Now, you can continue setting up your account.</p>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {clientConfiguration && clientConfiguration.enableSweepDocuSign && isDocusignIncomplete && (
                        <div>
                            <div className="row">
                                <div className="alert">
                                    <div className="alert--high-importance">
                                        <span className="icon-info"></span>
                                        <ul className="alert-list">
                                            <li className="alert-content">
                                                <div class="h4">Authorization Form Not Completed</div>
                                                <p>Your producer agreement and Sweep account Authorization was not completed. To continue setting up your sweep account, you'll need to sign this form using the link below.</p>
                                                <a onClick={(e) => handleDocusign(e)}>Sign Authorization Form</a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {clientConfiguration && !clientConfiguration.enableSweepDocuSign && (
                        <>
                            <div>
                                <gds-disclaimer className="gds-disclaimer">
                                    <h4>By enrolling your agency “sweep” payment services you agree to the following:</h4>
                                    <div slot="content">
                                        <ul className="list list--unordered">
                                            <li className="small-text">To allow GEICO to debit via ACH the bank account shown for customer policy payments whenever that option is selected by a user from your agency.</li>
                                            <li className="small-text">You are an authorized user of the bank account shown with authority, on behalf of your agency, to enroll in this service.</li>
                                            <li className="small-text">To allow GEICO to charge your agency a fee if any debit from the agency bank account is unsuccessful due to any of the following reasons: insufficient funds, account closed, no bank account/unable to locate the account, payment stopped, or bank account frozen. The fee amount is $20, or such other amount as provided by notice from GEICO from time-to-time. Fees may be deducted from commissions payable to the Agency.</li>
                                            <li className="small-text">The “sweep” services are offered by GEICO at its sole discretion and GEICO may discontinued at any time for any reason.</li>
                                            <li className="small-text">Your agency’s participation in “sweep” services is at GEICO’s sole discretion and may discontinued at any time for any reason upon notice from GEICO.</li>
                                            <li className="small-text">GEICO’s right to debit funds from your agency’s account is not contingent upon your agency’s receipt of valid funds from our mutual customer.</li>
                                            <li className="small-text">Any refunds issued by GEICO in consideration of an insurance policy will be issued to the policyholder regardless of payments being made via the “sweep” service.</li>
                                        </ul>
                                    </div>
                                </gds-disclaimer>
                            </div>
                            <div>
                                <fieldset
                                    role="radiogroup"
                                    className={`form-field radio-button-group ${errors.enrollSweep && `form-field--error`
                                        }`}
                                >
                                    <div className="checkbox-wrapper">
                                        <div>
                                            <input
                                                id="enrollSweep"
                                                type="checkbox"
                                                {...register("enrollSweep")}
                                                onChange={handleCheckboxChange}
                                            />
                                            <label htmlFor="enrollSweep" className="checkbox">
                                                Enroll in Sweep Payment Services
                                            </label>
                                            {errors.enrollSweep && (
                                                <span className="form-message">
                                                    {errors.enrollSweep?.message}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                </fieldset>
                            </div>
                        </>
                    )}
                </div>

                {clientConfiguration && !clientConfiguration.enableSweepDocuSign && (
                    <div className="row">
                        <div className="col-sm-12">
                            <button
                                type="submit"
                                className="btn btn--primary btn--full-mobile btn--pull-right"
                                onClick={handleNextClick}
                            >
                                <span>Next</span>
                            </button>
                            <Link to={`/profiles`} className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn" state={{ agencyProfile: agencyProfile }}>Cancel</Link>
                        </div>
                    </div>
                )}
                {isDocusignCompleted && clientConfiguration && clientConfiguration.enableSweepDocuSign && (
                    <div className="row">
                        <div className="col-sm-12">
                            <button
                                type="submit"
                                className="btn btn--primary btn--full-mobile btn--pull-right"
                                onClick={handleNextClick}
                            >
                                <span>Next</span>
                            </button>
                            <Link to={`/profiles`} className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn" state={{ agencyProfile: agencyProfile }}>Cancel</Link>
                        </div>
                    </div>
                )}

                {!isDocusignCompleted && clientConfiguration && clientConfiguration.enableSweepDocuSign && (
                    <div className="row">
                        <div className="col-sm-12">
                            <button
                                type="submit"
                                className="btn btn--secondary btn--full-mobile btn--pull-right"
                                disabled
                            >
                                <span>Next</span>
                            </button>
                            <Link to={`/profiles`} className="btn btn--destructive btn--full-mobile btn--pull-right expandable-form-cancel-btn" state={{ agencyProfile: agencyProfile }}>Cancel</Link>
                        </div>
                    </div>
                )}
            </div>
        </Layout >
    )
};


export default EmailSettings;